import React, { useState, useEffect } from 'react';
import { Alert, ConfigService, TransactionService } from '../../services';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import Pagination from '../../components/Pagination';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-tooltip/dist/react-tooltip.css';
import SearchInput from '../../components/SearchInput';
import { formatValue } from '../../utils/Helper';
import TableComponent from '../TableComponent';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import StatusBadge from '../StatusBadge';
import SortByFilter from '../listing/SortByFilter';
import { DownloadIcon } from '../icons/IconProvider';

const TransactionHistory = ({
  isTabStatus,
  setTabStatus,
  filterFiatData,
  setFilterFiatData,
  filterOnChainData,
  setFilterOnChainData,
  searchData,
  refreshTrigger,
}) => {
  const [fiatTransactionList, setFiatTransactionList] = useState([]);
  const [onChainTransaction, setOnChainTransactionList] = useState([]);
  const [, setPage] = useState(1);
  const [, setTotalPages] = useState(1);
  const [isFiatLoader, setFiatLoader] = useState(false);
  const [isOnchainPagination, setOnchainPagination] = useState({
    totalPages: 5,
    currentPage: 1,
  });

  const [isFiatPagination, setFiatPagination] = useState({
    totalPages: 5,
    currentPage: 1,
  });

  useEffect(() => {
    (async () => {
      await getTransactionList();
    })();
  }, [filterFiatData, filterOnChainData, refreshTrigger]);

  const getTransactionList = async () => {
    try {
      setFiatLoader(true);
      let fiatResponse = await TransactionService.getFiatTransactionList(
        filterFiatData.perPage,
        filterFiatData.page,
        filterFiatData.commonSearch,
        filterFiatData.date,
        filterFiatData.user_id,
        filterFiatData.status,
      );
      if (fiatResponse.status === 'failure') {
        setFiatLoader(false);
        Alert.error(fiatResponse.message);
        return false;
      }
      if (isTabStatus === 1) {
        setFiatPagination({
          totalPages: fiatResponse.data.pagination.totalPages,
          currentPage: fiatResponse.data.pagination.page,
        });
      }
      setFiatTransactionList([...fiatResponse.data.records]);
      setTotalPages(fiatResponse.data.pagination.totalPages);
      setPage(fiatResponse.data.pagination.page);

      let onchainResponse = await TransactionService.getOnchainTransactionList(
        filterOnChainData.perPage,
        filterOnChainData.page,
        filterOnChainData.commonSearch,
        filterOnChainData.sortBy,
        filterOnChainData.user_id,
        filterOnChainData.type,
      );

      if (onchainResponse.status === 'failure') {
        setFiatLoader(false);
        Alert.error(onchainResponse.message);
        return false;
      }
      if (isTabStatus === 2) {
        setOnchainPagination({
          totalPages: onchainResponse.data.pagination.totalPages,
          currentPage: onchainResponse.data.pagination.page,
        });
      }
      setOnChainTransactionList([...onchainResponse.data.records]);
      setTotalPages(onchainResponse.data.pagination.totalPages);
      setPage(onchainResponse.data.pagination.page);
      setFiatLoader(false);
    } catch (error) {
      setFiatLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  const handlePagination = (current) => {
    if (isTabStatus === 1) {
      setFilterFiatData({
        ...filterFiatData,
        page: current,
      });
    } else {
      setFilterOnChainData({
        ...filterOnChainData,
        page: current,
      });
    }
  };

  const tabSwitch = async (type) => {
    if (type === 1) {
      setTabStatus(1);
      setFilterFiatData({
        ...filterFiatData,
        page: 1,
        commonSearch: '',
        status: '',
      });
      await getTransactionList();
    } else {
      setTabStatus(2);
      setFilterOnChainData({
        ...filterOnChainData,
        page: 1,
        commonSearch: '',
        type: '',
      });
      await getTransactionList();
    }
  };

  const tableConfigFiat = {
    columns: {
      transaction_type: {
        title: 'Transaction Type',
        render: (data) => {
          return (
            <span className="bg-[#85919F] px-2.5 py-1 rounded-md text-white font-semibold inline-block uppercase text-xs ">
              {data?.type ? data.type : 'N/A'}
            </span>
          );
        },
      },
      transactions_id: {
        title: 'Transaction Id',
        render: (data) => {
          return data.transactions_id ? data.transactions_id : 'N/A';
        },
      },
      payment_method: {
        title: 'Payment Method',
        render: (data) => {
          const paymentAmt = Number(data?.amount);
          const voucherAmt = Number(data?.voucherUtilize?.discount_amount);
          return paymentAmt > 0 && voucherAmt > 0 ? (
            <>
              <span className="font-medium">
                {data?.payment_method +
                  ': ' +
                  formatValue(paymentAmt, {
                    decimalPlaces: 2,
                  })}
              </span>
              <span className="block font-medium">
                {'voucher: ' +
                  formatValue(voucherAmt, {
                    decimalPlaces: 2,
                  })}
              </span>
            </>
          ) : (
            <span className="font-medium">{data?.payment_method ?? 'N/A'}</span>
          );
        },
      },
      project: {
        title: 'Project',
        render: (data) => {
          return (
            <div className="flex items-center font-semibold text-md">
              <img
                src={data?.projectDetail?.projectsType?.icon_url}
                className="mr-2"
                alt=""
              />
              {data?.projectDetail?.name || '--'}
            </div>
          );
        },
      },
      amount: {
        title: 'Amount',
        render: (data) => {
          return (
            <span className="font-medium inline-block">
              {data?.total_amount
                ? formatValue(data?.total_amount, {
                    decimalPlaces: 2,
                  })
                : '0'}
            </span>
          );
        },
      },
      date: {
        title: 'Date',
        render: (data) => {
          return (
            <span className="font-medium inline-block">
              {ConfigService.dateFormat(data?.created_at, true)}
            </span>
          );
        },
      },
      status: {
        title: 'Status',
        render: (data) => {
          return <StatusBadge status={data?.status} />;
        },
      },
      action: {
        title: 'Action',
        render: (data) => {
          const pdfUrl = data?.pdf_url;
          const tooltipId = `action_download_tooltip_${data?.id}`;

          return (
            <div className="relative inline-block">
              {pdfUrl && (
                <>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tooltip-id={tooltipId}
                  >
                    <DownloadIcon />
                  </a>
                  <ReactTooltip
                    id={tooltipId}
                    place="top"
                    content="Purchase Order"
                  />
                </>
              )}
            </div>
          );
        },
      },
    },
    data: fiatTransactionList,
  };

  const tableConfigOnchain = {
    columns: {
      transaction_type: {
        title: 'Transaction Type',
        render: (data) => {
          return (
            <span className="bg-[#85919F] px-2.5 py-2 rounded-md text-white font-semibold inline-block uppercase text-xs ">
              {data?.type || '--'}
            </span>
          );
        },
      },
      project: {
        title: 'Project',
        render: (data) => {
          return (
            <div className="flex items-center font-semibold text-md">
              <img
                src={data?.projectDetail?.projectsType?.icon_url}
                className="mr-2"
                alt=""
              />
              {data?.projectDetail?.name || '--'}
            </div>
          );
        },
      },
      from: {
        title: 'From',
        render: (data) => {
          return (
            <>
              <div
                data-tooltip-id={`from_wallet_address${data?.id}`}
                className="break-all w-28 text-BlueText"
              >
                {data?.fromUser?.id === 1
                  ? 'Issuer'
                  : data?.fromUser?.full_name || 'NA'}
              </div>
              <ReactTooltip
                id={`from_wallet_address${data?.id}`}
                place="bottom"
                content={data?.from_wallet_address}
              />
            </>
          );
        },
      },
      to: {
        title: 'To',
        render: (data) => {
          return (
            <>
              <div
                data-tooltip-id={`to_wallet_address${data?.id}`}
                className="break-all w-36 text-BlueText"
              >
                {data?.to_wallet_address
                  ? ConfigService.shortenAddress(data.to_wallet_address)
                  : '--'}
              </div>
              <ReactTooltip
                id={`to_wallet_address${data?.id}`}
                place="bottom"
                content={data?.to_wallet_address}
              />
            </>
          );
        },
      },
      count: {
        title: 'Count',
        render: (data) => {
          return (
            <span className="inline-block font-semibold text-brandBlue">
              {data?.quantity || '--'}
            </span>
          );
        },
      },
      date: {
        title: 'Date',
        render: (data) => {
          return ConfigService.dateFormat(data?.created_at, true);
        },
      },
      status: {
        title: 'Status',
        render: (data) => {
          return (
            <StatusBadge
              status={
                data?.status === 1 ? 'transfer_completed' : 'transfer_pending'
              }
            />
          );
        },
      },
      link: {
        title: 'Link',
        render: (data) => {
          return (
            <>
              <div className="break-all w-28 text-BlueText">
                <a
                  rel="noreferrer"
                  target="_blank"
                  href={data?.txnHashURL || '#'}
                  className="flex items-center"
                >
                  <div className="w-24 overflow-ellipsis whitespace-nowrap overflow-hidden">
                    {data?.hash || '--'}
                  </div>
                  <img
                    src="/img/linkdirection-icon.svg"
                    className="ml-2"
                    alt=""
                  />
                </a>
              </div>
            </>
          );
        },
      },
    },
    data: onChainTransaction,
  };
  const fiatStatusOptions = [
    { name: 'Filter By', value: '' },
    { name: 'Created', value: 'created' },
    { name: 'Pending', value: 'pending' },
    { name: 'Completed', value: 'completed' },
    { name: 'Failed', value: 'failed' },
  ];

  const onChainTypeOptions = [
    { name: 'Sort By', value: '' },
    { name: 'Purchase', value: 'purchase' },
    { name: 'Transfer', value: 'transfer' },
  ];
  return (
    <div className="md:pb-5 my-4">
      <div className="bg-brandGray p-4 rounded-md">
        <div>
          <div className="lg:flex items-center justify-between gap-2 pb-4 mb-6 border-b border-[#B6BDC5]">
            <div>
              <h3 className="mb-4 lg:mb-0">Transaction History</h3>
            </div>
            <div className="md:flex items-center block">
              <div className="text-right mb-2 md:mb-0">
                <div className="md:mx-2 w-full md:w-72 max-w-full inline-flex relative">
                  <SearchInput
                    className="h-[42px]"
                    placeholder="Search project"
                    name="commonSearch"
                    id="commonSearch"
                    onChange={(e) => {
                      setPage(1);
                      searchData();
                      if (isTabStatus === 1) {
                        setFilterFiatData({
                          ...filterFiatData,
                          page: 1,
                          commonSearch: e.target.value,
                        });
                      } else {
                        setFilterOnChainData({
                          ...filterOnChainData,
                          page: 1,
                          commonSearch: e.target.value,
                        });
                      }
                    }}
                    value={
                      isTabStatus === 1
                        ? filterFiatData.commonSearch
                        : filterOnChainData.commonSearch
                    }
                  />
                </div>
              </div>
              <div className="inline-flex relative md:w-auto w-full">
                <SortByFilter
                  value={
                    isTabStatus === 1
                      ? filterFiatData.status
                      : filterOnChainData.type
                  }
                  dropdownList={
                    isTabStatus === 1 ? fiatStatusOptions : onChainTypeOptions
                  }
                  filterData={
                    isTabStatus === 1 ? filterFiatData : filterOnChainData
                  }
                  setFilterData={
                    isTabStatus === 1 ? setFilterFiatData : setFilterOnChainData
                  }
                  setPage={setPage}
                  fieldName={isTabStatus === 1 ? 'status' : 'type'}
                />
              </div>
            </div>
          </div>
          <div>
            <Tabs>
              <div className="grid grid-cols-2">
                <TabList className="flex">
                  <Tab
                    onClick={() => tabSwitch(1)}
                    className="rounded-md py-3 px-4 md:px-4 cursor-pointer"
                  >
                    Fiat Transaction{' '}
                  </Tab>
                  <Tab
                    onClick={() => tabSwitch(2)}
                    className="rounded-md py-3 px-4 md:px-4 cursor-pointer"
                  >
                    On Chain Transaction
                  </Tab>
                </TabList>
              </div>
              <TabPanel>
                <div className="flex flex-col relative overflow-x-auto w-full sm:rounded-lg">
                  <TableComponent
                    tableConfig={tableConfigFiat}
                    isListLoader={isFiatLoader}
                    isList={fiatTransactionList}
                  />
                </div>
                {fiatTransactionList &&
                  fiatTransactionList.length > 0 &&
                  isFiatLoader === false && (
                    <Pagination
                      total={isFiatPagination.totalPages}
                      current={isFiatPagination.currentPage}
                      pagination={(crPage) => handlePagination(crPage)}
                    />
                  )}
              </TabPanel>
              <TabPanel>
                <div className="flex flex-col relative overflow-x-auto w-full sm:rounded-lg">
                  <TableComponent
                    tableConfig={tableConfigOnchain}
                    isListLoader={isFiatLoader}
                    isList={onChainTransaction}
                  />

                  {onChainTransaction &&
                    onChainTransaction.length > 0 &&
                    isFiatLoader === false && (
                      <Pagination
                        total={isOnchainPagination.totalPages}
                        current={isOnchainPagination.currentPage}
                        pagination={(crPage) => handlePagination(crPage)}
                      />
                    )}
                </div>
              </TabPanel>
            </Tabs>
            <div></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
